import { useEffect, useMemo, useState } from 'react';
import { http } from '../../lib/api';
import { downloadCsv } from '../../lib/csv';
import { AppLayout } from '../../components/layout';
import { Button, MetricCard, Card } from '../../components/core';
import { DataTable, type Column, SpinnerBlock } from '../../components/feedback';
import { ChartCard, PALETTE, type ChartData } from '../../components/charts';
import { ADMIN_NAV } from './nav';
import type { AdminReportsDto } from '@livestreamx/shared-types';

const fmt = (n: number) => n.toLocaleString();
const fmtCur = (n: number) =>
  n.toLocaleString('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 0 });
const hrs = (secs: number) => (secs / 3600).toLocaleString(undefined, { maximumFractionDigits: 1 });
const gb = (bytes: number) => (bytes / 1_000_000_000).toLocaleString(undefined, { maximumFractionDigits: 2 });
const dayLabel = (date: string) => new Date(date + 'T00:00:00Z').toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

const usageCols: Column<AdminReportsDto['usage']['perTenant'][number]>[] = [
  { key: 'tenant', header: 'Tenant', render: (r) => <span className="font-medium">{r.tenantName}</span> },
  { key: 'plan', header: 'Plan', render: (r) => <span className="text-muted text-sm">{r.planName ?? '—'}</span> },
  { key: 'sessions', header: 'Sessions', render: (r) => <span>{fmt(r.sessions)}</span> },
  { key: 'hours', header: 'Hours', render: (r) => <span>{hrs(r.durationSeconds)}</span> },
  { key: 'data', header: 'Data (GB)', render: (r) => <span>{gb(r.bytes)}</span> },
  { key: 'peak', header: 'Peak bitrate', render: (r) => <span className="text-muted text-sm">{r.peakBitrateKbps ? `${fmt(r.peakBitrateKbps)} kbps` : '—'}</span> },
];

const planMixCols: Column<AdminReportsDto['growth']['planMix'][number]>[] = [
  { key: 'plan', header: 'Plan', render: (r) => <span className="font-medium">{r.planName}</span> },
  { key: 'count', header: 'Subscriptions', render: (r) => <span>{fmt(r.count)}</span> },
];

const revenueCols: Column<AdminReportsDto['revenue']['monthly'][number]>[] = [
  { key: 'month', header: 'Month', render: (r) => <span className="font-medium">{r.month}</span> },
  { key: 'amount', header: 'Revenue', render: (r) => <span>{fmtCur(r.amount)}</span> },
  { key: 'count', header: 'Payments', render: (r) => <span>{fmt(r.count)}</span> },
];

function CsvButton({ filename, header, rows }: { filename: string; header: string[]; rows: (string | number | null)[][] }) {
  return (
    <Button variant="secondary" size="sm" disabled={rows.length === 0} onClick={() => downloadCsv(filename, header, rows)}>
      ⭳ Download CSV
    </Button>
  );
}

export function AdminReportsPage() {
  const [data, setData] = useState<AdminReportsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    http.get<AdminReportsDto>('/api/admin/reports')
      .then(setData).catch((e) => setError(e.message ?? 'Failed')).finally(() => setLoading(false));
  }, []);

  const sessionsChart = useMemo<ChartData | null>(
    () => data ? { type: 'bar', labels: data.usage.daily.map((d) => dayLabel(d.date)), values: data.usage.daily.map((d) => d.sessions), label: 'Sessions', yLabel: 'Sessions', color: '#6366f1' } : null,
    [data],
  );
  const signupsChart = useMemo<ChartData | null>(
    () => data && data.growth.signupsPerMonth.length ? { type: 'line', labels: data.growth.signupsPerMonth.map((s) => s.month), values: data.growth.signupsPerMonth.map((s) => s.count), label: 'New tenants', yLabel: 'Tenants', color: '#22c55e' } : null,
    [data],
  );
  const planChart = useMemo<ChartData | null>(
    () => data && data.growth.planMix.length ? { type: 'doughnut', labels: data.growth.planMix.map((p) => p.planName), values: data.growth.planMix.map((p) => p.count), colors: PALETTE } : null,
    [data],
  );
  const revenueChart = useMemo<ChartData | null>(
    () => data && data.revenue.monthly.length ? { type: 'bar', labels: data.revenue.monthly.map((r) => r.month), values: data.revenue.monthly.map((r) => r.amount), label: 'Revenue', yLabel: 'Revenue', color: '#f59e0b' } : null,
    [data],
  );

  return (
    <AppLayout brand="LiveStreamX" navGroups={ADMIN_NAV} variant="admin">
      <div className="admin-page">
        <div className="page-header admin-page-header">
          <div>
            <h1 className="page-title">Reports</h1>
            <p className="page-subtitle">Usage, growth and revenue across the platform</p>
          </div>
        </div>
        {loading && <SpinnerBlock />}
        {error && <div className="alert danger">{error}</div>}
        {data && (
          <>
            <div className="stats-grid">
              <MetricCard label="Sessions (30d)" value={fmt(data.usage.daily.reduce((a, d) => a + d.sessions, 0))} tone="primary" hint="Streaming sessions, last 30 days" />
              <MetricCard label="Hours streamed" value={hrs(data.usage.totalDurationSeconds)} tone="info" hint="All-time" />
              <MetricCard label="Data transferred" value={`${gb(data.usage.totalBytes)} GB`} tone="warning" hint="All-time" />
              <MetricCard label="Avg peak bitrate" value={data.usage.avgPeakBitrateKbps ? `${fmt(data.usage.avgPeakBitrateKbps)} kbps` : '—'} tone="success" hint="Across sessions" />
              <MetricCard label="Revenue (total)" value={fmtCur(data.revenue.totalAmount)} tone="primary" hint="Paid payments" />
              <MetricCard label="Tenant signups" value={fmt(data.growth.signupsPerMonth.reduce((a, s) => a + s.count, 0))} tone="danger" hint="All-time" />
            </div>

            <ChartCard title="Streaming Activity — Sessions per Day (30 Days)" data={sessionsChart} />
            <ChartCard title="Tenant Signups per Month" data={signupsChart} />
<Card title="Usage per Tenant">
              <div className="flex justify-between items-center mb-2">
                <span className="text-muted text-sm">Streamed hours, data and peak ingest bitrate per tenant.</span>
                <CsvButton
                  filename="usage-per-tenant.csv"
                  header={['Tenant', 'Plan', 'Sessions', 'Hours', 'Data (GB)', 'Peak bitrate (kbps)']}
                  rows={data.usage.perTenant.map((r) => [r.tenantName, r.planName, r.sessions, Number(hrs(r.durationSeconds)), Number(gb(r.bytes)), r.peakBitrateKbps])}
                />
              </div>
              <DataTable columns={usageCols} rows={data.usage.perTenant} />
            </Card>

            <Card title="Plan Mix, Tenant & Subscription Status">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                <div>
                  <ChartCard title="Subscriptions by Plan" data={planChart} />
                  <DataTable columns={planMixCols} rows={data.growth.planMix} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <p className="text-muted text-sm" style={{ margin: 0 }}>Tenant status</p>
                  {data.growth.tenantsByStatus.map((s) => (
                    <div key={s.status} className="flex justify-between" style={{ borderBottom: '1px solid var(--border)', paddingBottom: 6 }}>
                      <span className="text-sm font-medium">{s.status}</span><span>{fmt(s.count)}</span>
                    </div>
                  ))}
                  <p className="text-muted text-sm" style={{ margin: '12px 0 0' }}>Subscription status</p>
                  {data.growth.subscriptionsByStatus.map((s) => (
                    <div key={s.status} className="flex justify-between" style={{ borderBottom: '1px solid var(--border)', paddingBottom: 6 }}>
                      <span className="text-sm font-medium">{s.status}</span><span>{fmt(s.count)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Card>

            <Card title="Revenue">
              <div className="flex justify-between items-center mb-2">
                <span className="text-muted text-sm">Paid payments by month. Empty until subscriptions are charged or tenants are provisioned.</span>
                <CsvButton
                  filename="revenue-monthly.csv"
                  header={['Month', 'Revenue', 'Payments']}
                  rows={data.revenue.monthly.map((r) => [r.month, r.amount.toFixed(2), r.count])}
                />
              </div>
              <ChartCard title="Revenue per Month" data={revenueChart} />
              <DataTable columns={revenueCols} rows={data.revenue.monthly} />
              {data.revenue.monthly.length === 0 && (
                <p className="text-muted text-sm" style={{ margin: '12px 0 0' }}>
                  No paid payments recorded yet — revenue appears here once tenants subscribe via the billing flow or are provisioned from the Tenants page.
                </p>
              )}
            </Card>
          </>
        )}
      </div>
    </AppLayout>
  );
}
